export type ReplaceDecision = 'discard' | 'cancel';

// Wraps <dialog id="bundle-replace-confirm-dialog"> (snippets/bundle-replace-confirm-dialog.liquid).
// Mirrors the open/close animation pattern used by Dialog (modules/dialog.ts) and
// the promise-based prompt() shape used by NameDialog (modules/name-dialog.ts).
export class ReplaceConfirmDialog {
  private discardBtn: HTMLButtonElement;
  private cancelBtn: HTMLButtonElement;
  private resolveFn: ((value: ReplaceDecision) => void) | null = null;

  constructor(private dialogEl: HTMLDialogElement) {
    this.discardBtn = dialogEl.querySelector('.bundle-replace-confirm-dialog__discard') as HTMLButtonElement;
    this.cancelBtn = dialogEl.querySelector('.bundle-replace-confirm-dialog__cancel') as HTMLButtonElement;

    this.discardBtn.addEventListener('click', () => this.settle('discard'));
    this.cancelBtn.addEventListener('click', () => this.settle('cancel'));

    this.dialogEl.addEventListener('click', (e) => {
      if (e.target === this.dialogEl) this.settle('cancel');
    });
    this.dialogEl.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.settle('cancel');
    });
  }

  confirm(): Promise<ReplaceDecision> {
    // Resolve any still-pending confirm before starting a new one, so a second
    // call can never leak the first call's promise.
    this.resolveFn?.('cancel');

    this.dialogEl.classList.remove('is-closing');
    this.dialogEl.showModal();
    document.body.classList.add('--no-scroll');

    return new Promise((resolve) => {
      this.resolveFn = resolve;
    });
  }

  private settle(value: ReplaceDecision) {
    const resolve = this.resolveFn;
    this.resolveFn = null;
    this.dialogEl.classList.add('is-closing');
    setTimeout(() => {
      this.dialogEl.close();
      document.body.classList.remove('--no-scroll');
    }, 300);
    resolve?.(value);
  }
}

// Exposed on window so bundle-list.ts and bundle-favorite-popup.ts — separate
// Vite entrypoints that can't import bundle-tool.ts or each other directly —
// can check/act on the builder's in-progress state before navigating away
// from it. Set once by BundlePage.init() (bundle-tool.ts); consumers must
// guard with `?.` since it's only present on /pages/bundle.
export interface BundleReplaceGuard {
  hasUnsaved: () => boolean;
  confirm: () => Promise<ReplaceDecision>;
}

declare global {
  interface Window {
    kaseMeBundleReplaceGuard?: BundleReplaceGuard;
  }
}
